import { typedKeys } from '../../utils/TypedKeys';
// eslint-disable-next-line import/no-cycle
import {
  isInsertFormatterActive,
  isSelectionWrapped,
  isSelectionWrappedAdjacent,
  linkFormatter,
  MarkupFunction,
  markupInsertFunction,
  markupMultilineFunction,
  markupWrappingFunction
} from './MarkupFormatters';

export const lflf = '\n\n';

/** Ordered in the order in which they should appear */
export type FormatterType =
  'Bold' |
  'Italic' |
  'Underline' |
  'Paragraph' |
  'Heading1' |
  'Heading2' |
  'Heading3' |
  'UnorderedList' |
  'OrderedList' |
  'Link';

const dispatchChangeEvent = (textAreaElement: HTMLTextAreaElement, value: string): void => {
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set;
  nativeInputValueSetter?.call(textAreaElement, value);

  const inputEvent = new InputEvent('input', { bubbles: true });
  textAreaElement.dispatchEvent(inputEvent);
};

export const applyMarkup = (
  formatterType: FormatterType,
  textAreaElement: HTMLTextAreaElement,
): void => {
  const { selection, preSelection, postSelection } = getSelectionAndAdjacents(textAreaElement);

  const map: FormatterMapping = FormatterMap[formatterType];

  const markupFunction = getMarkupFunction(map.formatterApplicationType);
  const { replacement, selectionPos } = markupFunction(preSelection, selection, postSelection, map.formatter, textAreaElement, map.prepend);

  // using document.execCommand('insertText') allows for undo/redo, but it doesn't work in ff
  // 5 yr old bug: https://bugzilla.mozilla.org/show_bug.cgi?id=1220696
  if (!document.execCommand('insertText', false, replacement)) {
    // returns false in ff though, so we can manually set the value this way
    // We need to get updated selection info, because in some cases, it has changed
    const { preSelection: updatedPreSelection, postSelection: updatedPostSelection } = getSelectionAndAdjacents(textAreaElement);

    const newValue = updatedPreSelection + replacement + updatedPostSelection;
    dispatchChangeEvent(textAreaElement, newValue);
  }

  // We blur before focusing to cause the textarea to correctly scroll the cursor/selection into view
  textAreaElement.blur();
  textAreaElement.focus();
  textAreaElement.selectionStart = selectionPos[0];
  textAreaElement.selectionEnd = selectionPos[0] + selectionPos[1];
};

const getSelectionAndAdjacents = (textAreaElement: HTMLTextAreaElement)
  : { selection: string, preSelection: string, postSelection: string } => {
  if (!textAreaElement) { return { selection: '', preSelection: '', postSelection: '' }; }

  const value = textAreaElement.value || '';

  const selectionStart = textAreaElement.selectionStart;
  const selectionEnd = textAreaElement.selectionEnd;
  const pre = value.slice(0, selectionStart);
  const selection = value.slice(selectionStart, selectionEnd);
  const post = value.slice(selectionEnd);

  return { selection, preSelection: pre, postSelection: post };
};

export const getActiveFormatters = (textAreaElement: HTMLTextAreaElement | null): FormatterType[] => {
  const result: FormatterType[] = [];
  const wrappingTypes: FormatterType[] = getFormatterTypesOfKind('wrapping');
  const insertTypes: FormatterType[] = getFormatterTypesOfKind('insert');

  if (!textAreaElement) { return result; }

  const { selection, preSelection, postSelection } = getSelectionAndAdjacents(textAreaElement);

  wrappingTypes.concat(insertTypes).forEach(formatterType => {
    const formatter = FormatterMap[formatterType].formatter;
    switch (FormatterMap[formatterType].formatterApplicationType) {
      case 'wrapping':
        if (isSelectionWrapped(selection, formatter) || isSelectionWrappedAdjacent(preSelection, postSelection, formatter)) {
          result.push(formatterType);
        }
        break;

      case 'insert':
        if (isInsertFormatterActive(preSelection, selection, formatter)) {
          result.push(formatterType);
        }
        break;

      default:
        return;
    }
  });

  return result;
};

export const getFormatterTypesOfKind = (kind: FormatterApplicationType): FormatterType[] =>
  typedKeys(FormatterMap).filter(type => FormatterMap[type].formatterApplicationType === kind);

export type KeyboardShortcut = {
  /** numeric keyCode used to identify the key */
  keyCode: number;
  /** string representation of the key, used to display the shortcut */
  keyLabel: string;
  /** whether or not shift key is required for the shortcut */
  shiftKey?: boolean;
};

export type FormatterApplicationType = 'insert' | 'wrapping' | 'multiline';

function getMarkupFunction(type: FormatterApplicationType): MarkupFunction {
  switch (type) {
    case 'insert':
      return markupInsertFunction;

    case 'multiline':
      return markupMultilineFunction;

    case 'wrapping':
    default:
      return markupWrappingFunction;
  }
}

export type FormatterMapping = {
  /** string representation of the formatter used to translate into markup */
  formatter: string,
  /** How the formatter is applied to the string */
  formatterApplicationType: FormatterApplicationType,
  /** optional string to prepend - Used to ensure a blank line precedes the markup. */
  prepend?: string,
  /** defines a keyboard shortcut for applying the formatter */
  keyboardShortcut?: KeyboardShortcut,
};

export const FormatterMap: { [key in FormatterType]: FormatterMapping } = {
  Bold: {
    formatter: '**',
    formatterApplicationType: 'wrapping',
    keyboardShortcut: { keyLabel: 'b', keyCode: 66 },
  },
  Italic: {
    formatter: '__',
    formatterApplicationType: 'wrapping',
    keyboardShortcut: { keyLabel: 'i', keyCode: 73 },
  },
  Underline: {
    formatter: '+',
    formatterApplicationType: 'wrapping',
    keyboardShortcut: { keyLabel: 'u', keyCode: 85 },
  },
  Paragraph: {
    formatter: 'p. ',
    formatterApplicationType: 'insert',
    prepend: lflf,
  },
  Heading1: {
    formatter: 'h1. ',
    formatterApplicationType: 'insert',
    prepend: lflf,
  },
  Heading2: {
    formatter: 'h2. ',
    formatterApplicationType: 'insert',
    prepend: lflf,
  },
  Heading3: {
    formatter: 'h3. ',
    formatterApplicationType: 'insert',
    prepend: lflf,
  },
  UnorderedList: {
    formatter: '* ',
    formatterApplicationType: 'multiline',
    keyboardShortcut: {
      keyLabel: '7',
      keyCode: 56,
      shiftKey: true,
    },
  },
  OrderedList: {
    formatter: '# ',
    formatterApplicationType: 'multiline',
    keyboardShortcut: {
      keyLabel: '8',
      keyCode: 55,
      shiftKey: true,
    },
  },
  Link: {
    formatter: linkFormatter,
    formatterApplicationType: 'insert',
    keyboardShortcut: { keyLabel: 'k', keyCode: 75 },
  }
};

export const FormattersWithShortcuts: FormatterType[] = typedKeys(FormatterMap).filter(type => FormatterMap[type].keyboardShortcut);

export const ShortcutKeys: number[] = FormattersWithShortcuts.map(value => FormatterMap[value].keyboardShortcut!.keyCode);

export const getFormatterTypeFromShortcut = (e: React.KeyboardEvent<HTMLTextAreaElement>): (FormatterType | undefined) => {
  const matchingKey: (string | undefined) = FormattersWithShortcuts.find(key => {
    const shiftMatch = !!FormatterMap[key].keyboardShortcut?.shiftKey === e.shiftKey;
    const keyMatch = FormatterMap[key].keyboardShortcut?.keyCode === e.keyCode;

    return shiftMatch && keyMatch;
  });

  return matchingKey as FormatterType;
};
